import { useState } from 'react'

import type { MemoryItem } from '../../api/memory'
import Button from '../Button'

interface MemoryListProps {
  memories: MemoryItem[]
  onDelete: (id: string) => void
  onEdit: (id: string, text: string) => void
}

function excerpt(text: string, maxLength = 40) {
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text
}

/** `metadata.reflectsMemoryIds` is stored as a JSON-encoded string array (Task 25). */
function parseReflectsMemoryIds(metadata: Record<string, unknown> | undefined): string[] {
  const raw = metadata?.['reflectsMemoryIds']
  if (typeof raw !== 'string') return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function isReflection(item: MemoryItem): boolean {
  return item.metadata?.['memoryType'] === 'reflection'
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

export function MemoryList({ memories, onDelete, onEdit }: MemoryListProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (memories.length === 0) {
    return (
      <p className="text-sm text-harbor-text-secondary">
        No memories yet. Memories accumulate as agents record decisions and preferences.
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {memories.map((item) => (
        <li key={item.id} className="rounded-lg border border-harbor-border bg-harbor-surface p-3">
          {editingId === item.id ? (
            <div className="space-y-2">
              <textarea
                className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised p-2 text-sm text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                aria-label="Memory text"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    onEdit(item.id, draft)
                    setEditingId(null)
                  }}
                  aria-label={`Save memory: ${excerpt(item.memory)}`}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingId(null)}
                  aria-label={`Cancel editing memory: ${excerpt(item.memory)}`}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {isReflection(item) && (
                    <span className="rounded-full bg-harbor-accent/10 px-2 py-0.5 text-xs font-medium text-harbor-accent">
                      Reflection
                    </span>
                  )}
                  <p className="text-sm text-harbor-text">{item.memory}</p>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-harbor-text-muted">
                  {item.createdByUsername ? (
                    <span title={`Created by ${item.createdByUsername}`}>
                      by {item.createdByUsername}
                    </span>
                  ) : null}
                  {(() => {
                    const ts = formatTimestamp(item.createdAt ?? item.updatedAt)
                    return ts ? (
                      <span title={item.createdAt ?? item.updatedAt ?? undefined}>{ts}</span>
                    ) : null
                  })()}
                  {item.score !== undefined && <span>relevance {item.score.toFixed(2)}</span>}
                </div>
                {isReflection(item) && parseReflectsMemoryIds(item.metadata).length > 0 && (
                  <div className="mt-1">
                    <button
                      type="button"
                      className="text-xs text-harbor-text-muted underline"
                      onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                      aria-expanded={expandedId === item.id}
                      aria-label={`Show memories synthesized into: ${excerpt(item.memory)}`}
                    >
                      Synthesized from {parseReflectsMemoryIds(item.metadata).length} memories
                    </button>
                    {expandedId === item.id && (
                      <ul className="mt-1 list-inside list-disc text-xs text-harbor-text-muted">
                        {parseReflectsMemoryIds(item.metadata).map((sourceId) => (
                          <li key={sourceId}>{sourceId}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(item.id)
                    setDraft(item.memory)
                  }}
                  aria-label={`Edit memory: ${excerpt(item.memory)}`}
                >
                  Edit
                </Button>
                {confirmingId === item.id ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      onDelete(item.id)
                      setConfirmingId(null)
                    }}
                    aria-label={`Confirm forgetting memory: ${excerpt(item.memory)}`}
                  >
                    Confirm
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmingId(item.id)}
                    aria-label={`Forget memory: ${excerpt(item.memory)}`}
                  >
                    Forget
                  </Button>
                )}
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}
